import ChatRounded from "@mui/icons-material/ChatRounded";
import PersonRemoveRounded from "@mui/icons-material/PersonRemoveRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  authorLabel,
  MISSING,
  playerHandleSuffix,
} from "@bw/shared/components";
import type {
  ConversationResponse,
  FriendRequestItem,
  FriendSummaryResponse,
  UserSearchResultResponse,
} from "@bw/shared/types/generated";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { initials } from "../../../common/avatar";
import { Page } from "../../../common/components/Page";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { ListSkeleton } from "../components/ListSkeleton";
import { useCreateConversation } from "../hooks/useCreateConversation";
import { useFriendActions } from "../hooks/useFriendActions";
import { useFriendRequests } from "../hooks/useFriendRequests";
import { useFriends } from "../hooks/useFriends";
import { useUserSearch } from "../hooks/useUserSearch";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function FriendsPage() {
  const navigate = useNavigate();
  const requestsQ = useFriendRequests();
  const friendsQ = useFriends();
  const {
    accept,
    decline,
    cancel,
    unfriend,
    sendRequest,
    isRequestBusy,
    isFriendBusy,
    isSendBusy,
    resetCreate,
    resetInboxErrors,
  } = useFriendActions();
  const createConversation = useCreateConversation(resetInboxErrors);
  resetCreate.current = createConversation.reset;

  const [searchInput, setSearchInput] = useState("");
  const [committedQuery, setCommittedQuery] = useState("");
  const debouncedInput = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  useEffect(() => setCommittedQuery(debouncedInput), [debouncedInput]);

  const trimmedQuery = committedQuery.trim();
  const searchActive = trimmedQuery.length >= MIN_SEARCH_LENGTH;
  const searchQ = useUserSearch(committedQuery);
  const incoming: FriendRequestItem[] = requestsQ.data?.incoming ?? [];
  const outgoing: FriendRequestItem[] = requestsQ.data?.outgoing ?? [];
  const friends: FriendSummaryResponse[] = friendsQ.data ?? [];
  const friendIds = useMemo(
    () => new Set(friends.map((friend) => friend.user_id)),
    [friends],
  );
  const outgoingIds = useMemo(
    () => new Set(outgoing.map((request) => request.user_id)),
    [outgoing],
  );
  const searchResults: UserSearchResultResponse[] = searchQ.data ?? [];
  const actionError =
    accept.error ??
    decline.error ??
    cancel.error ??
    unfriend.error ??
    createConversation.error;
  const inboxAndListLoading = requestsQ.isLoading && friendsQ.isLoading;

  const goToConversation = (conversation: ConversationResponse): void => {
    const id = conversation.conversation_id;
    if (typeof id === "string" && id.trim()) {
      navigate(`/community/messages/${id}`);
    }
  };
  const isMessageBusy = (userId: string): boolean =>
    createConversation.isPending && createConversation.variables === userId;

  return (
    <Page title="Friends" narrow>
      <Stack spacing={2.5}>
        {friendsQ.data ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: -2 }}>
            {friends.length === 1 ? "1 friend" : `${friends.length} friends`}
          </Typography>
        ) : null}
        <Stack component="section" aria-label="Add a friend" spacing={1}>
          {sendRequest.isError ? (
            <GuardianAwareError error={sendRequest.error}>
              <Alert severity="error" role="alert">
                {sendErrorMessage(sendRequest.error)}
              </Alert>
            </GuardianAwareError>
          ) : null}
          <BoxSearch
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={() => setCommittedQuery(searchInput)}
          />
          {!searchActive ? null : searchQ.isError ? (
            <Alert severity="error" role="alert">
              Couldn&apos;t search right now. Please try again.
            </Alert>
          ) : searchQ.isPending || searchQ.isFetching ? (
            <Typography color="text.secondary" role="status">
              Searching…
            </Typography>
          ) : searchResults.length === 0 ? (
            <EmptyState>No players found.</EmptyState>
          ) : (
            <List disablePadding>
                {searchResults.map((result) => {
                  const relationship = friendIds.has(result.user_id)
                    ? "friend"
                    : outgoingIds.has(result.user_id)
                      ? "requested"
                      : "none";
                  const action =
                    relationship === "friend" ? (
                      <Chip label="Friends" size="small" />
                    ) : relationship === "requested" ? (
                      <Chip label="Requested" size="small" />
                    ) : (
                      <Button
                        type="button"
                        variant="contained"
                        size="small"
                        sx={{ borderRadius: 999 }}
                        disabled={isSendBusy(result.user_id)}
                        onClick={() => sendRequest.mutate(result.user_id)}
                      >
                        Add
                      </Button>
                    );
                  return (
                    <PersonRow
                      key={result.user_id}
                      displayName={result.display_name}
                      userId={result.user_id}
                      avatarUrl={result.avatar_url}
                      actions={action}
                    />
                  );
                })}
            </List>
          )}
        </Stack>

        {actionError ? (
          <GuardianAwareError error={actionError}>
            <Alert severity="error" role="alert">
              That didn&apos;t work. Please try again.
            </Alert>
          </GuardianAwareError>
        ) : null}

        {inboxAndListLoading ? (
          <ListSkeleton />
        ) : (
          <>
            <FriendsSection title="Requests" ariaLabel="Friend requests">
              {requestsQ.isError ? (
                <Alert severity="error" role="alert">
                  Couldn&apos;t load your friend requests. Please try again.
                </Alert>
              ) : requestsQ.isLoading ? (
                <ListSkeleton />
              ) : incoming.length === 0 && outgoing.length === 0 ? (
                <EmptyState>No pending requests.</EmptyState>
              ) : (
                  <List disablePadding>
                    {incoming.map((request) => {
                      const busy = isRequestBusy(request.friendship_id);
                      return (
                        <PersonRow
                          key={request.friendship_id}
                          displayName={request.display_name}
                          userId={request.user_id}
                          avatarUrl={request.avatar_url}
                          actions={
                            <>
                              <Button
                                type="button"
                                variant="contained"
                                size="small"
                                disabled={busy}
                                sx={{ borderRadius: 999 }}
                                onClick={() =>
                                  accept.mutate(request.friendship_id)
                                }
                              >
                                Accept
                              </Button>
                              <Button
                                type="button"
                                variant="text"
                                color="inherit"
                                size="small"
                                disabled={busy}
                                onClick={() =>
                                  decline.mutate(request.friendship_id)
                                }
                              >
                                Decline
                              </Button>
                            </>
                          }
                        />
                      );
                    })}
                    {outgoing.map((request) => (
                      <PersonRow
                        key={request.friendship_id}
                        displayName={request.display_name}
                        userId={request.user_id}
                        avatarUrl={request.avatar_url}
                        actions={
                          <Button
                            type="button"
                            variant="text"
                            size="small"
                            disabled={isRequestBusy(request.friendship_id)}
                            onClick={() => cancel.mutate(request.friendship_id)}
                          >
                            Cancel
                          </Button>
                        }
                      />
                    ))}
                  </List>
              )}
            </FriendsSection>

            <FriendsSection title="Friends" ariaLabel="Friends">
              {friendsQ.isError ? (
                <Alert severity="error" role="alert">
                  Couldn&apos;t load your friends. Please try again.
                </Alert>
              ) : friendsQ.isLoading ? (
                <ListSkeleton />
              ) : friends.length === 0 ? (
                <EmptyState>No friends yet.</EmptyState>
              ) : (
                  <List disablePadding>
                    {friends.map((friend) => (
                      <PersonRow
                        key={friend.user_id}
                        displayName={friend.display_name}
                        userId={friend.user_id}
                        avatarUrl={friend.avatar_url}
                        actions={
                          <>
                            <IconButton
                              type="button"
                              aria-label="Message"
                              title="Message"
                              disabled={isMessageBusy(friend.user_id)}
                              onClick={() =>
                                createConversation.mutate(friend.user_id, {
                                  onSuccess: goToConversation,
                                })
                              }
                            >
                              <ChatRounded fontSize="small" />
                            </IconButton>
                            <IconButton
                              type="button"
                              aria-label="Unfriend"
                              title="Unfriend"
                              disabled={isFriendBusy(friend.user_id)}
                              onClick={() => unfriend.mutate(friend.user_id)}
                              sx={{ color: "text.secondary" }}
                            >
                              <PersonRemoveRounded fontSize="small" />
                            </IconButton>
                          </>
                        }
                      />
                    ))}
                  </List>
              )}
            </FriendsSection>
          </>
        )}

      </Stack>
    </Page>
  );
}

function BoxSearch({
  value,
  onChange,
  onSubmit,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
}) {
  return (
    <Stack
      component="form"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <TextField
        type="search"
        placeholder="Search players by name"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        fullWidth
        size="small"
        slotProps={{
          htmlInput: { "aria-label": "Search players by name" },
          input: {
            sx: { borderRadius: 999 },
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
    </Stack>
  );
}

function FriendsSection({
  title,
  ariaLabel,
  children,
}: {
  title: string;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <Stack component="section" spacing={0.5} aria-label={ariaLabel}>
      <SectionHeader title={title} />
      {children}
    </Stack>
  );
}

function EmptyState({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
      {children}
    </Typography>
  );
}

function PersonRow({
  displayName,
  userId,
  avatarUrl,
  actions,
}: {
  displayName: string | null | undefined;
  userId: string;
  avatarUrl: string | null | undefined;
  actions: ReactNode;
}) {
  const name = authorLabel(displayName, userId);
  return (
    <ListItem disablePadding sx={{ gap: 1.5, py: 0.75, px: { xs: 0.5, sm: 1 } }}>
      <Avatar src={avatarUrl ?? undefined} alt="" aria-hidden sx={{ width: 44, height: 44, fontSize: 16, fontWeight: 700 }}>
        {name !== MISSING ? initials(name) : null}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: "0.9375rem", fontWeight: 700 }}>
          <Typography component="span" sx={{ font: "inherit" }}>{name}</Typography>
          {name !== MISSING ? (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {` ${playerHandleSuffix(userId)}`}
            </Typography>
          ) : null}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", flexShrink: 0 }}>
        {actions}
      </Stack>
    </ListItem>
  );
}

function sendErrorMessage(error: unknown): string {
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.trim()
    ? message
    : "Couldn't send that request. Please try again.";
}
